import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, FileText, AlertCircle, ChevronLeft } from 'lucide-react';
import SEOHead, { generateBreadcrumbSchema } from '../components/SEOHead';

// Public security posture. Deliberately generic — vendor names, AI model
// stack, and program economics live in our private trust report (gated
// via compliance@theailedger.ai). Enterprise buyers + procurement teams
// request it directly. Casual visitors + competitors don't get a stack
// rundown for free.

const PRACTICES = [
  { icon: Lock, label: 'Encryption', body: 'TLS 1.2+ in transit, AES-256 at rest. HSTS enforced on every connection.' },
  { icon: Shield, label: 'Access control', body: 'Multi-factor authentication required for all administrative access. Every admin action is audit-logged.' },
  { icon: Shield, label: 'Payment data', body: 'Card details are processed entirely by Stripe (PCI DSS Level 1). We never see, store, or transmit them.' },
  { icon: AlertCircle, label: 'Monitoring', body: 'Continuous monitoring of administrative endpoints with anomaly alerting and bot defense (Cloudflare).' },
];

const COMPLIANCE = [
  { label: 'Entity', value: 'theailedger LLC (Washington, USA)' },
  { label: 'GDPR / UK GDPR', value: 'DSAR portal operating; DPA available on request' },
  { label: 'CCPA / CPRA', value: 'Privacy request portal at /privacy/request-my-data' },
  { label: 'DMCA / 17 USC §512', value: 'Designated agent — dmca@theailedger.ai · /legal/dmca' },
  { label: 'PCI DSS', value: 'Out of scope — Stripe is the merchant of record' },
];

export default function Security() {
  return (
    <div className="min-h-screen bg-black text-white" data-testid="security-page">
      <SEOHead
        title="Security & Trust — theailedger.ai"
        description="theailedger.ai security and compliance posture. GDPR, CCPA, and DMCA disclosures in one place."
        path="/security"
        keywords="theailedger security, AI marketplace trust, GDPR, CCPA, DMCA agent"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebPage',
              name: 'Security & Trust — theailedger.ai',
              url: 'https://theailedger.ai/security',
              description: 'Security and compliance posture for theailedger.ai.',
              about: ['security', 'GDPR', 'CCPA', 'DMCA'],
            },
            generateBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Security & Trust', path: '/security' },
            ]),
          ],
        }}
      />

      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-[#00F0FF] font-bold tracking-tight" data-testid="security-home-link">
            theailedger.ai
          </Link>
          <Link to="/" className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-1 transition-colors">
            <ChevronLeft className="w-4 h-4" />
            Back to marketplace
          </Link>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-14 pb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 text-xs font-semibold mb-6">
          <Shield className="w-3.5 h-3.5" />
          Trust center
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-5">
          Security &amp; trust posture
        </h1>
        <p className="text-base sm:text-lg text-white/70 leading-relaxed">
          theailedger.ai is operated by <span className="text-white">theailedger LLC</span>, a
          Washington State limited liability company. We hold ourselves to standard SaaS security
          practices and the data-protection laws of the jurisdictions our customers operate in.
          A detailed trust report is available to enterprise prospects on request.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-12" data-testid="security-practices">
        <h2 className="text-base text-[#00F0FF] uppercase font-bold tracking-wide mb-4">Security practices</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {PRACTICES.map(({ icon: Icon, label, body }) => (
            <div key={label} className="bg-neutral-900/40 border border-white/10 rounded-xl p-5">
              <Icon className="w-5 h-5 text-[#00F0FF] mb-3" />
              <div className="font-bold mb-1">{label}</div>
              <p className="text-sm text-neutral-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-12" data-testid="security-compliance">
        <h2 className="text-base text-[#00F0FF] uppercase font-bold tracking-wide mb-4">Compliance &amp; legal posture</h2>
        <div className="border border-white/10 rounded-xl divide-y divide-white/10">
          {COMPLIANCE.map((c) => (
            <div key={c.label} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 p-4">
              <span className="text-sm font-semibold text-white">{c.label}</span>
              <span className="text-sm text-neutral-300">{c.value}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-12" data-testid="security-trust-report">
        <div className="bg-gradient-to-br from-[#00F0FF]/5 to-transparent border border-[#00F0FF]/30 rounded-xl p-6">
          <h2 className="text-base text-[#00F0FF] uppercase font-bold tracking-wide mb-3">Enterprise trust report</h2>
          <p className="text-sm text-neutral-300 leading-relaxed mb-4">
            Procurement, IT, or compliance team evaluating theailedger.ai? Request our full trust report —
            includes sub-processor list (GDPR Art. 28(2)), DPA template, security questionnaire,
            and incident-response policy.
          </p>
          <a
            href="mailto:compliance@theailedger.ai?subject=Trust%20report%20request"
            data-testid="security-trust-report-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00F0FF] text-black font-semibold hover:bg-[#00F0FF]/90 transition"
          >
            <FileText className="w-4 h-4" />
            Request trust report
          </a>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-12">
        <h2 className="text-base text-[#00F0FF] uppercase font-bold tracking-wide mb-4">Legal documents</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Link
            to="/legal/dmca"
            data-testid="security-dmca-link"
            className="flex items-center gap-3 p-4 bg-white/[0.03] border border-white/10 hover:border-[#00F0FF]/40 rounded-xl transition"
          >
            <FileText className="w-5 h-5 text-[#00F0FF]" />
            <div>
              <div className="font-semibold text-white">DMCA takedown policy</div>
              <div className="text-xs text-neutral-500">Submit a copyright notice</div>
            </div>
          </Link>
          <Link
            to="/privacy/request-my-data"
            data-testid="security-dsar-link"
            className="flex items-center gap-3 p-4 bg-white/[0.03] border border-white/10 hover:border-[#00F0FF]/40 rounded-xl transition"
          >
            <FileText className="w-5 h-5 text-[#00F0FF]" />
            <div>
              <div className="font-semibold text-white">Request my data (DSAR)</div>
              <div className="text-xs text-neutral-500">Access, deletion, export, correction</div>
            </div>
          </Link>
          <Link
            to="/privacy"
            data-testid="security-privacy-link"
            className="flex items-center gap-3 p-4 bg-white/[0.03] border border-white/10 hover:border-[#00F0FF]/40 rounded-xl transition"
          >
            <FileText className="w-5 h-5 text-[#00F0FF]" />
            <div>
              <div className="font-semibold text-white">Privacy policy</div>
              <div className="text-xs text-neutral-500">What we collect and why</div>
            </div>
          </Link>
          <Link
            to="/terms"
            data-testid="security-terms-link"
            className="flex items-center gap-3 p-4 bg-white/[0.03] border border-white/10 hover:border-[#00F0FF]/40 rounded-xl transition"
          >
            <FileText className="w-5 h-5 text-[#00F0FF]" />
            <div>
              <div className="font-semibold text-white">Terms &amp; conditions</div>
              <div className="text-xs text-neutral-500">Marketplace + user agreement</div>
            </div>
          </Link>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-20">
        <div className="bg-white/[0.02] border border-white/10 rounded-xl p-5">
          <h2 className="text-base text-[#00F0FF] uppercase font-bold tracking-wide mb-2">Report a vulnerability</h2>
          <p className="text-sm text-neutral-300 leading-relaxed">
            If you believe you&apos;ve found a security issue affecting theailedger.ai, please email{' '}
            <a href="mailto:security@theailedger.ai" className="text-[#00F0FF] font-semibold hover:underline">
              security@theailedger.ai
            </a>{' '}
            with reproduction steps. We aim to acknowledge reports within 48 hours and patch
            verified issues as quickly as possible.
          </p>
        </div>
      </section>
    </div>
  );
}
